<template>
  <VDialogV3
    id="manufacturer-card"
    position="right"
    width="600px"
    height="100vh"
    :entity="localManufacturer"
    :file-entities="fileEntities"
    :context-menu="ManufacturersService.contextMenu"
    :submit-method="submit"
  >
    <section>
      <VInput
        ref="nameInput"
        v-model="localManufacturer.name"
        label="Название производителя"
        layout="vertical"
        required
        :max-length="200"
        :validate-method="validateName"
      />
    </section>

    <br>

    <section class="trade-marks">
      <h4>Торговые марки</h4>

      <!-- Строка — выбор существующей марки или ввод новой: новая уходит на бэк
           только с name бэк создаёт её сам. Дубли не проверяем: уникальность держит бэк и вернёт ошибку -->
      <div
        v-for="(tradeMark, index) in localManufacturer.tradeMarks"
        :key="index"
        class="trade-mark-row"
      >
        <VSuggestionSelectV2
          v-model="localManufacturer.tradeMarks[index]"
          :type="TradeMark"
          :data="({ pattern }: { pattern: string }) => TradeMarksService.getList({ pattern })"
          placeholder="Название торговой марки"
          required
          :validate-method="validateTradeMarkName"
        />
        <VButton
          material="delete"
          title="Удалить"
          @click="localManufacturer.tradeMarks.splice(index, 1)"
        />
      </div>

      <VButton
        theme="link"
        material="add"
        @click="addTradeMarkRow"
      >
        Добавить торговую марку
      </VButton>
    </section>
  </VDialogV3>
</template>

<style lang="scss" src="./ManufacturerCard.scss" />

<script lang="ts">

import Manufacturer            from '@/domains/Manufacturers/Manufacturer';
import ManufacturersService    from '@/domains/Manufacturers/ManufacturersService';
import TradeMark               from '@/domains/TradeMarks/TradeMark';
import TradeMarksService       from '@/domains/TradeMarks/TradeMarksService';
import EntityPointer           from '@/domains/EntityPointer/EntityPointer';

export default defineComponent({
  name: 'ManufacturerCard',

  props: {
    manufacturer: {
      type:    Manufacturer as PropType<Manufacturer | null>,
      default: null,
    },
    id: {
      type:    Number,
      default: null,
    },
  },

  setup() {
    return {
      ManufacturersService,
      TradeMark,
      TradeMarksService,
    };
  },

  data() {
    return {
      localManufacturer: null as Manufacturer | null,

      /** Сохранённое название — своё же название дублем не считается */
      initialName: null as string | null,

      /** Производитель как открыли — с ним сравниваем, были ли изменения (isManufacturerChanged) */
      initialManufacturer: null as Manufacturer | null,
    };
  },

  computed: {

    /**
     * Есть ли что сохранять — глубокое сравнение с производителем как открыли (как в StoreCard).
     * Перестановку марок или повторный выбор той же марки сочтёт изменением —
     * тогда уйдёт лишний update с теми же данными, это безвредно.
     */
    isManufacturerChanged(): boolean {
      return this.$core.utils.isEqual(this.localManufacturer, this.initialManufacturer) === false;
    },

    /** Указатели на сущности, к которым цепляются файлы: только сам производитель. Пустые отсеиваются. */
    fileEntities(): EntityPointer[] {
      return EntityPointer.collect([
        EntityPointer.make('manufacturer', this.localManufacturer?.id, this.localManufacturer?.toString()),
      ]);
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    async init(): Promise<void> {
      if (this.id) {
        this.localManufacturer = await ManufacturersService.get(this.id);
      }
      else if (this.manufacturer instanceof Manufacturer) {
        this.localManufacturer = this.$core.utils.cloneDeep(this.manufacturer);
      }
      else {
        this.localManufacturer = new Manufacturer();
      }

      this.initialName         = this.localManufacturer.name;
      this.initialManufacturer = this.$core.utils.cloneDeep(this.localManufacturer);
    },

    /**
     * Сохранение. Ничего не меняли — запрос не шлём и возвращаем true:
     * VDialogV3 закроет карточку как после сохранения, без «Производитель обновлён».
     */
    async submit(): Promise<boolean> {
      if (this.isManufacturerChanged === false) {
        return true;
      }
      else if (this.localManufacturer!.isExisting === true) {
        return await ManufacturersService.update(this.localManufacturer!);
      }
      else {
        return await ManufacturersService.add(this.localManufacturer!);
      }
    },

    /** Пустая строка под новую марку: незаполненную не пропустит required у VSuggestionSelectV2 */
    addTradeMarkRow(): void {
      (this.localManufacturer!.tradeMarks as (TradeMark | null)[]).push(null);
    },

    validateName(value: string): string | null | Promise<string | null> {
      // Пустое и пробелы ловит required, сюда только чтобы не слать запрос
      if (this.$core.utils.string.isNotEmptyString(value) === false) { return null; }
      if (value === this.initialName) { return null; }

      return ManufacturersService.getList({ name: value }).then(list => {
        return list.length > 0 ? 'Ошибка уникальности. Данное название уже существует' : null;
      });
    },

    /**
     * Свободный ввод приходит объектом TradeMark (fromSuggestion), а не строкой:
     * required объект считает заполненным, поэтому марку из пробелов ловим здесь по name.
     * null ловит сам required — тут молчим, чтобы ошибка не задвоилась.
     */
    validateTradeMarkName(value: TradeMark | null): string | null {
      if (value !== null && this.$core.utils.string.isNotEmptyString(value.name) === false) {
        return this.$core.utils.validators.MESSAGES.required;
      }

      return null;
    }
  },
});
</script>
